import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Search, Plus, Eye, Edit, Trash2, Upload, ExternalLink, 
  User, Mail, Phone, MapPin, Briefcase, Languages, Tag, 
  FileText, RefreshCw, Filter, X, Building, Calendar, Users, Database,
  Download, UserPlus, CheckSquare, Tags
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const DEFAULT_SOURCES = [
  'Direct Apply', 'LinkedIn', 'Referral', 'Job Board', 'Headhunted', 'Talent Pool Import', 'Career Fair', 'Agency'
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'bg-green-100 text-green-800' },
  { value: 'passive', label: 'Passive', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'do_not_contact', label: 'Do Not Contact', color: 'bg-red-100 text-red-800' }
];

const SPECIALIZATIONS = [
  'HR', 'Finance', 'Engineering', 'IT', 'Sales', 'Marketing', 'Operations', 
  'Legal', 'Consulting', 'Healthcare', 'Education', 'Management', 'Other'
];

const DOMAINS = [
  'Oil & Gas', 'Banking', 'Telecom', 'Healthcare', 'Manufacturing', 
  'Retail', 'Technology', 'Real Estate', 'Government', 'NGO', 'Other'
];

export default function AdminCandidatesPage() {
  const { language } = useLanguage();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  
  // Filters
  const [search, setSearch] = useState('');
  const [filterSpec, setFilterSpec] = useState('');
  const [filterDomain, setFilterDomain] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMinExp, setFilterMinExp] = useState('');
  const [filterMaxExp, setFilterMaxExp] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterCandidateType, setFilterCandidateType] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    nationality: '',
    current_location: '',
    gender: '',
    linkedin_url: '',
    cv_url: '',
    years_of_experience: '',
    functional_specialization: '',
    industry_domains: [],
    languages: [],
    skills: [],
    education: '',
    tags: [],
    source: 'Direct Apply',
    recruiter_notes: '',
    talent_pool_status: 'active'
  });
  
  // Temp inputs for array fields
  const [newSkill, setNewSkill] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newLang, setNewLang] = useState({ language: '', level: 'Intermediate' });
  const [newDomain, setNewDomain] = useState('');

  // Selection & Bulk Actions
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkTagDialogOpen, setBulkTagDialogOpen] = useState(false);
  const [applyToJobDialogOpen, setApplyToJobDialogOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkTags, setBulkTags] = useState({ add: [], remove: [] });
  const [newBulkTag, setNewBulkTag] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 25 });
      if (search) params.append('search', search);
      if (filterSpec && filterSpec !== 'all') params.append('specialization', filterSpec);
      if (filterDomain && filterDomain !== 'all') params.append('domain', filterDomain);
      if (filterSource && filterSource !== 'all') params.append('source', filterSource);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterMinExp) params.append('min_experience', filterMinExp);
      if (filterMaxExp) params.append('max_experience', filterMaxExp);
      if (filterLocation) params.append('location', filterLocation);
      if (filterCandidateType && filterCandidateType !== 'all') params.append('candidate_type', filterCandidateType);
      
      const response = await fetch(`${API}/admin/candidates?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCandidates(data.candidates || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterSpec, filterDomain, filterSource, filterStatus, filterMinExp, filterMaxExp, filterLocation, filterCandidateType]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/candidates/stats`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchSources = async () => {
    try {
      const response = await fetch(`${API}/admin/ats/sources`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSources(data.sources || DEFAULT_SOURCES);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/admin/ats/jobs?status=active`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setJobs(data.jobs || []);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  useEffect(() => {
    fetchCandidates();
    fetchStats();
    fetchSources();
    fetchJobs();
  }, [fetchCandidates]);

  const resetFilters = () => {
    setSearch('');
    setFilterSpec('');
    setFilterDomain('');
    setFilterSource('');
    setFilterStatus('');
    setFilterMinExp('');
    setFilterMaxExp('');
    setFilterLocation('');
    setPage(1);
    setSelectedIds([]);
  };

  const resetForm = () => {
    setForm({
      full_name: '',
      email: '',
      phone: '',
      nationality: '',
      current_location: '',
      gender: '',
      linkedin_url: '',
      cv_url: '',
      years_of_experience: '',
      functional_specialization: '',
      industry_domains: [],
      languages: [],
      skills: [],
      education: '',
      tags: [],
      source: 'Direct Apply',
      recruiter_notes: '',
      talent_pool_status: 'active'
    });
    setNewSkill('');
    setNewTag('');
    setNewLang({ language: '', level: 'Intermediate' });
    setNewDomain('');
  };

  const handleViewCandidate = async (candidate) => {
    try {
      const response = await fetch(`${API}/admin/candidates/${candidate.candidate_id}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSelectedCandidate(data);
        setForm({
          ...data,
          years_of_experience: data.years_of_experience || '',
          industry_domains: data.industry_domains || [],
          languages: data.languages || [],
          skills: data.skills || [],
          tags: data.tags || []
        });
        setEditing(false);
        setDetailDialogOpen(true);
      }
    } catch (error) {
      toast.error('Failed to load candidate details');
    }
  };

  const handleCreateCandidate = async () => {
    if (!form.full_name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        years_of_experience: form.years_of_experience ? parseInt(form.years_of_experience) : null
      };
      
      const response = await fetch(`${API}/admin/candidates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Candidate created successfully');
        setCreateDialogOpen(false);
        resetForm();
        fetchCandidates();
        fetchStats();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to create candidate');
      }
    } catch (error) {
      toast.error('Failed to create candidate');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCandidate = async () => {
    if (!selectedCandidate) return;
    
    setSaving(true);
    try {
      const payload = {
        ...form,
        years_of_experience: form.years_of_experience ? parseInt(form.years_of_experience) : null
      };
      
      const response = await fetch(`${API}/admin/candidates/${selectedCandidate.candidate_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success('Candidate updated successfully');
        setEditing(false);
        handleViewCandidate(selectedCandidate);
        fetchCandidates();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update candidate');
      }
    } catch (error) {
      toast.error('Failed to update candidate');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (!window.confirm('Are you sure you want to delete this candidate? This will also remove all their job applications.')) return;
    
    try {
      const response = await fetch(`${API}/admin/candidates/${candidateId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Candidate deleted');
        setDetailDialogOpen(false);
        fetchCandidates();
        fetchStats();
      } else {
        toast.error('Failed to delete candidate');
      }
    } catch (error) {
      toast.error('Failed to delete candidate');
    }
  };

  // Selection handlers
  const toggleSelectAll = () => {
    if (selectedIds.length === candidates.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(candidates.map(c => c.candidate_id));
    }
  };

  const toggleSelect = (candidateId) => {
    setSelectedIds(prev => 
      prev.includes(candidateId) 
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
  };

  // Bulk Actions
  const handleBulkApplyToJob = async () => {
    if (!selectedJobId) {
      toast.error('Please select a job');
      return;
    }
    
    setBulkProcessing(true);
    try {
      const response = await fetch(`${API}/admin/candidates/bulk/apply-to-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          candidate_ids: selectedIds,
          job_id: selectedJobId,
          recruiter_notes: bulkNotes
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setApplyToJobDialogOpen(false);
        setSelectedIds([]);
        setSelectedJobId('');
        setBulkNotes('');
        fetchCandidates();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to apply candidates');
      }
    } catch (error) {
      toast.error('Failed to apply candidates to job');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkTag = async () => {
    if (bulkTags.add.length === 0 && bulkTags.remove.length === 0) {
      toast.error('Please add or remove at least one tag');
      return;
    }
    
    setBulkProcessing(true);
    try {
      const response = await fetch(`${API}/admin/candidates/bulk/tag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          candidate_ids: selectedIds,
          tags_to_add: bulkTags.add,
          tags_to_remove: bulkTags.remove
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setBulkTagDialogOpen(false);
        setSelectedIds([]);
        setBulkTags({ add: [], remove: [] });
        fetchCandidates();
      } else {
        toast.error('Failed to update tags');
      }
    } catch (error) {
      toast.error('Failed to update tags');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    setBulkProcessing(true);
    try {
      const response = await fetch(`${API}/admin/candidates/bulk/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          candidate_ids: selectedIds,
          status: newStatus
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedIds([]);
        fetchCandidates();
        fetchStats();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      let url = `${API}/admin/candidates/export?format=${format}`;
      if (selectedIds.length > 0) {
        url += `&candidate_ids=${selectedIds.join(',')}`;
      } else {
        // Apply current filters
        if (filterSpec && filterSpec !== 'all') url += `&specialization=${filterSpec}`;
        if (filterDomain && filterDomain !== 'all') url += `&domain=${filterDomain}`;
        if (filterStatus && filterStatus !== 'all') url += `&status=${filterStatus}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
      }
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        
        if (format === 'csv') {
          // Download CSV
          const blob = new Blob([data.content], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `candidates_export_${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
          toast.success(`Exported ${data.count} candidates`);
        } else {
          // JSON download
          const blob = new Blob([JSON.stringify(data.candidates, null, 2)], { type: 'application/json' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `candidates_export_${new Date().toISOString().split('T')[0]}.json`;
          link.click();
          toast.success(`Exported ${data.count} candidates`);
        }
      } else {
        toast.error('Failed to export candidates');
      }
    } catch (error) {
      toast.error('Failed to export candidates');
    }
  };

  const handleExportExcel = async () => {
    try {
      // Build URL with current filters
      let params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterSpec && filterSpec !== 'all') params.append('specialization', filterSpec);
      if (filterDomain && filterDomain !== 'all') params.append('domain', filterDomain);
      if (filterSource && filterSource !== 'all') params.append('source', filterSource);
      if (filterStatus && filterStatus !== 'all') params.append('status', filterStatus);
      if (filterMinExp) params.append('min_experience', filterMinExp);
      if (filterMaxExp) params.append('max_experience', filterMaxExp);
      if (filterLocation) params.append('location', filterLocation);
      if (filterCandidateType && filterCandidateType !== 'all') params.append('candidate_type', filterCandidateType);
      
      const url = `${API}/admin/candidates/export/excel?${params}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const blob = await response.blob();
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `candidates_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        link.click();
        toast.success('Excel export downloaded');
      } else {
        toast.error('Failed to export to Excel');
      }
    } catch (error) {
      toast.error('Failed to export to Excel');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} candidates? This cannot be undone.`)) return;
    
    setBulkProcessing(true);
    try {
      const response = await fetch(`${API}/admin/candidates/bulk`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(selectedIds)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedIds([]);
        fetchCandidates();
        fetchStats();
      } else {
        toast.error('Failed to delete candidates');
      }
    } catch (error) {
      toast.error('Failed to delete candidates');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleCVUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API}/upload/cv`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setForm(prev => ({ ...prev, cv_url: data.path, cv_file_id: data.file_id }));
        toast.success('CV uploaded successfully');
      } else {
        toast.error('Failed to upload CV');
      }
    } catch (error) {
      toast.error('Failed to upload CV');
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !form.skills.includes(newSkill.trim())) {
      setForm(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setForm(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addTag = () => {
    if (newTag.trim() && !form.tags.includes(newTag.trim())) {
      setForm(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const addLanguage = () => {
    if (newLang.language.trim()) {
      setForm(prev => ({ ...prev, languages: [...prev.languages, { ...newLang }] }));
      setNewLang({ language: '', level: 'Intermediate' });
    }
  };

  const removeLanguage = (idx) => {
    setForm(prev => ({ ...prev, languages: prev.languages.filter((_, i) => i !== idx) }));
  };

  const addDomain = () => {
    if (newDomain.trim() && !form.industry_domains.includes(newDomain.trim())) {
      setForm(prev => ({ ...prev, industry_domains: [...prev.industry_domains, newDomain.trim()] }));
      setNewDomain('');
    }
  };

  const removeDomain = (domain) => {
    setForm(prev => ({ ...prev, industry_domains: prev.industry_domains.filter(d => d !== domain) }));
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(s => s.value === status);
    return opt ? <Badge className={opt.color}>{opt.label}</Badge> : <Badge className="bg-gray-100">{status}</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Form Fields Component
  const CandidateFormFields = () => (
    <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Full Name *</Label>
          <Input
            value={form.full_name}
            onChange={(e) => setForm(prev => ({ ...prev, full_name: e.target.value }))}
            placeholder="John Doe"
            data-testid="candidate-name"
          />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input
            type="email"
            value={form.email}
            onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
            placeholder="john@example.com"
            data-testid="candidate-email"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Phone</Label>
          <Input
            value={form.phone}
            onChange={(e) => setForm(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="+49 123 456 789"
          />
        </div>
        <div className="space-y-2">
          <Label>LinkedIn URL</Label>
          <Input
            value={form.linkedin_url}
            onChange={(e) => setForm(prev => ({ ...prev, linkedin_url: e.target.value }))}
            placeholder="https://linkedin.com/in/..."
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Current Location</Label>
          <Input
            value={form.current_location}
            onChange={(e) => setForm(prev => ({ ...prev, current_location: e.target.value }))}
            placeholder="Dubai, UAE"
          />
        </div>
        <div className="space-y-2">
          <Label>Nationality (Optional)</Label>
          <Input
            value={form.nationality}
            onChange={(e) => setForm(prev => ({ ...prev, nationality: e.target.value }))}
            placeholder="German"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Years of Experience</Label>
          <Input
            type="number"
            min="0"
            value={form.years_of_experience}
            onChange={(e) => setForm(prev => ({ ...prev, years_of_experience: e.target.value }))}
            placeholder="5"
          />
        </div>
        <div className="space-y-2">
          <Label>Specialization</Label>
          <Select value={form.functional_specialization} onValueChange={(v) => setForm(prev => ({ ...prev, functional_specialization: v }))}>
            <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              {SPECIALIZATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Gender (Private)</Label>
          <Select value={form.gender || ''} onValueChange={(v) => setForm(prev => ({ ...prev, gender: v }))}>
            <SelectTrigger><SelectValue placeholder="Optional..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Male">Male</SelectItem>
              <SelectItem value="Female">Female</SelectItem>
              <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
              <SelectItem value="Not specified">Not specified</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* CV Upload */}
      <div className="space-y-2">
        <Label>CV / Resume</Label>
        <div className="flex items-center gap-2">
          <Input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleCVUpload}
            className="flex-1"
          />
          {form.cv_url && (
            <a href={form.cv_url} target="_blank" rel="noopener noreferrer" className="text-[#33AADD] hover:underline flex items-center gap-1">
              <FileText className="h-4 w-4" /> View CV
            </a>
          )}
        </div>
      </div>
      
      {/* Industry Domains */}
      <div className="space-y-2">
        <Label>Industry Domains</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.industry_domains?.map((d, i) => (
            <Badge key={`skill-${skill || i}`} variant="secondary" className="flex items-center gap-1">
              {d}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeDomain(d)} />
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Select value={newDomain} onValueChange={setNewDomain}>
            <SelectTrigger className="flex-1"><SelectValue placeholder="Select domain..." /></SelectTrigger>
            <SelectContent>
              {DOMAINS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={addDomain}>Add</Button>
        </div>
      </div>
      
      {/* Languages */}
      <div className="space-y-2">
        <Label>Languages</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.languages?.map((l, i) => (
            <Badge key={`lang-${l?.language || i}`} variant="outline" className="flex items-center gap-1">
              {l.language} ({l.level})
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeLanguage(i)} />
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newLang.language}
            onChange={(e) => setNewLang(prev => ({ ...prev, language: e.target.value }))}
            placeholder="Language"
            className="flex-1"
          />
          <Select value={newLang.level} onValueChange={(v) => setNewLang(prev => ({ ...prev, level: v }))}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Native">Native</SelectItem>
              <SelectItem value="Fluent">Fluent</SelectItem>
              <SelectItem value="Advanced">Advanced</SelectItem>
              <SelectItem value="Intermediate">Intermediate</SelectItem>
              <SelectItem value="Basic">Basic</SelectItem>
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" onClick={addLanguage}>Add</Button>
        </div>
      </div>
      
      {/* Skills */}
      <div className="space-y-2">
        <Label>Skills</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.skills?.map((s, i) => (
            <Badge key={i} className="bg-blue-100 text-blue-800 flex items-center gap-1">
              {s}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeSkill(s)} />
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            placeholder="Add skill..."
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
          />
          <Button type="button" variant="outline" onClick={addSkill}>Add</Button>
        </div>
      </div>
      
      {/* Tags */}
      <div className="space-y-2">
        <Label>Tags</Label>
        <div className="flex flex-wrap gap-2 mb-2">
          {form.tags?.map((t, i) => (
            <Badge key={i} className="bg-purple-100 text-purple-800 flex items-center gap-1">
              {t}
              <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(t)} />
            </Badge>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            placeholder="Add tag..."
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
          />
          <Button type="button" variant="outline" onClick={addTag}>Add</Button>
        </div>
      </div>
      
      {/* Education & Notes */}
      <div className="space-y-2">
        <Label>Education</Label>
        <Textarea
          value={form.education}
          onChange={(e) => setForm(prev => ({ ...prev, education: e.target.value }))}
          placeholder="Education background..."
          rows={2}
        />
      </div>
      
      <div className="space-y-2">
        <Label>Recruiter Notes</Label>
        <Textarea
          value={form.recruiter_notes}
          onChange={(e) => setForm(prev => ({ ...prev, recruiter_notes: e.target.value }))}
          placeholder="Internal notes..."
          rows={3}
        />
      </div>
      
      {/* Source & Status */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Source</Label>
          <Select value={form.source} onValueChange={(v) => setForm(prev => ({ ...prev, source: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Talent Pool Status</Label>
          <Select value={form.talent_pool_status} onValueChange={(v) => setForm(prev => ({ ...prev, talent_pool_status: v }))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6" data-testid="admin-candidates-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111827] flex items-center gap-2">
            <Database className="h-6 w-6 text-[#33AADD]" />
            Candidates / Talent Pool
          </h1>
          <p className="text-[#6B7280]">Central candidate database with {total} profiles</p>
        </div>
        <Button onClick={() => { resetForm(); setCreateDialogOpen(true); }} data-testid="add-candidate-btn">
          <Plus className="h-4 w-4 mr-2" /> Add Candidate
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Total</p>
                  <p className="text-xl font-bold text-[#111827]">{stats.total}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Active</p>
                  <p className="text-xl font-bold text-[#111827]">{stats.active}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <Users className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Passive</p>
                  <p className="text-xl font-bold text-[#111827]">{stats.passive}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">This Week</p>
                  <p className="text-xl font-bold text-[#111827]">{stats.recent_week}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search & Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder="Search by name, email, skills..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="search-candidates"
              />
            </div>
            <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="h-4 w-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Filters'}
            </Button>
            <Button variant="ghost" onClick={resetFilters}>
              <RefreshCw className="h-4 w-4 mr-2" /> Reset
            </Button>
          </div>
          
          {showFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
              <Select value={filterSpec} onValueChange={(v) => { setFilterSpec(v); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Specialization" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Specializations</SelectItem>
                  {SPECIALIZATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterDomain} onValueChange={(v) => { setFilterDomain(v); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Domain" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Domains</SelectItem>
                  {DOMAINS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterSource} onValueChange={(v) => { setFilterSource(v); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Source" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  {sources.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setPage(1); }}>
                <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                placeholder="Location..."
                value={filterLocation}
                onChange={(e) => { setFilterLocation(e.target.value); setPage(1); }}
              />
              <Input
                type="number"
                placeholder="Min exp (years)"
                value={filterMinExp}
                onChange={(e) => { setFilterMinExp(e.target.value); setPage(1); }}
              />
              <Input
                type="number"
                placeholder="Max exp (years)"
                value={filterMaxExp}
                onChange={(e) => { setFilterMaxExp(e.target.value); setPage(1); }}
              />
              <Select value={filterCandidateType} onValueChange={(v) => { setFilterCandidateType(v); setPage(1); }}>
                <SelectTrigger data-testid="filter-candidate-type"><SelectValue placeholder="Candidate Type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="job_applicant">Job Applicants</SelectItem>
                  <SelectItem value="talent_pool_only">Talent Pool Only</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <Card className="mb-4 border-[#33AADD] bg-[#33AADD]/5">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <CheckSquare className="h-5 w-5 text-[#33AADD]" />
                <span className="font-medium text-[#111827]">{selectedIds.length} selected</span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>
                  Clear
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setApplyToJobDialogOpen(true)} data-testid="bulk-apply-to-job">
                  <UserPlus className="h-4 w-4 mr-1" /> Apply to Job
                </Button>
                <Button variant="outline" size="sm" onClick={() => setBulkTagDialogOpen(true)}>
                  <Tags className="h-4 w-4 mr-1" /> Manage Tags
                </Button>
                <Select onValueChange={handleBulkStatusChange} disabled={bulkProcessing}>
                  <SelectTrigger className="w-[140px] h-8">
                    <SelectValue placeholder="Change Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Set Active</SelectItem>
                    <SelectItem value="passive">Set Passive</SelectItem>
                    <SelectItem value="do_not_contact">Do Not Contact</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
                  <Download className="h-4 w-4 mr-1" /> Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={handleExportExcel}>
                  <Download className="h-4 w-4 mr-1" /> Export Excel
                </Button>
                <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Candidates Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD]"></div>
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-12 text-[#6B7280]">
              <Users className="h-12 w-12 mx-auto mb-4 text-[#E5E7EB]" />
              <p>No candidates found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedIds.length === candidates.length && candidates.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Specialization</TableHead>
                      <TableHead>Experience</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Applications</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {candidates.map((cand) => (
                      <TableRow 
                        key={cand.candidate_id} 
                        data-testid={`candidate-row-${cand.candidate_id}`}
                        className={selectedIds.includes(cand.candidate_id) ? 'bg-[#33AADD]/5' : ''}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(cand.candidate_id)}
                            onCheckedChange={() => toggleSelect(cand.candidate_id)}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-[#33AADD]/10 flex items-center justify-center text-[#33AADD] font-medium">
                              {cand.full_name?.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-[#111827]">{cand.full_name}</p>
                              <p className="text-sm text-[#6B7280]">{cand.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#6B7280]">{cand.current_location || '-'}</TableCell>
                        <TableCell>
                          {cand.functional_specialization && (
                            <Badge variant="outline">{cand.functional_specialization}</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-[#6B7280]">
                          {cand.years_of_experience ? `${cand.years_of_experience} yrs` : '-'}
                        </TableCell>
                        <TableCell className="text-[#6B7280]">{cand.source || '-'}</TableCell>
                        <TableCell>{getStatusBadge(cand.talent_pool_status)}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="secondary">{cand.application_count || 0}</Badge>
                              {cand.candidate_type === 'job_applicant' && (
                                <Badge className="bg-blue-100 text-blue-800 text-xs">Applicant</Badge>
                              )}
                              {cand.candidate_type === 'talent_pool_only' && (
                                <Badge className="bg-gray-100 text-gray-700 text-xs">Pool Only</Badge>
                              )}
                              {cand.candidate_type === 'both' && (
                                <Badge className="bg-purple-100 text-purple-800 text-xs">Both</Badge>
                              )}
                            </div>
                            {cand.latest_job_title && (
                              <span className="text-xs text-[#9CA3AF] truncate max-w-[150px]" title={cand.latest_job_title}>
                                Latest: {cand.latest_job_title}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => handleViewCandidate(cand)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-[#6B7280]">
                    Page {page} of {totalPages} ({total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                      Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Candidate</DialogTitle>
          </DialogHeader>
          <CandidateFormFields />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateCandidate} disabled={saving} data-testid="save-candidate-btn">
              {saving ? 'Saving...' : 'Create Candidate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail/Edit Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{editing ? 'Edit Candidate' : 'Candidate Profile'}</span>
              <div className="flex gap-2">
                {!editing && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteCandidate(selectedCandidate?.candidate_id)}>
                      <Trash2 className="h-4 w-4 mr-1" /> Delete
                    </Button>
                  </>
                )}
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {editing ? (
            <>
              <CandidateFormFields />
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={handleUpdateCandidate} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </>
          ) : selectedCandidate && (
            <div className="space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Profile Header */}
              <div className="flex items-start gap-4 p-4 bg-[#F9FAFB] rounded-lg">
                <div className="h-16 w-16 rounded-full bg-[#33AADD]/10 flex items-center justify-center text-[#33AADD] text-2xl font-medium">
                  {selectedCandidate.full_name?.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-[#111827]">{selectedCandidate.full_name}</h3>
                  <p className="text-[#6B7280]">{selectedCandidate.functional_specialization || 'Not specified'}</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {getStatusBadge(selectedCandidate.talent_pool_status)}
                    {selectedCandidate.source && <Badge variant="outline">{selectedCandidate.source}</Badge>}
                    {selectedCandidate.years_of_experience && (
                      <Badge variant="secondary">{selectedCandidate.years_of_experience} years exp</Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-[#6B7280]">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${selectedCandidate.email}`} className="text-[#33AADD] hover:underline">{selectedCandidate.email}</a>
                </div>
                {selectedCandidate.phone && (
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <Phone className="h-4 w-4" />
                    {selectedCandidate.phone}
                  </div>
                )}
                {selectedCandidate.current_location && (
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <MapPin className="h-4 w-4" />
                    {selectedCandidate.current_location}
                  </div>
                )}
                {selectedCandidate.linkedin_url && (
                  <div className="flex items-center gap-2 text-[#6B7280]">
                    <ExternalLink className="h-4 w-4" />
                    <a href={selectedCandidate.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[#33AADD] hover:underline">LinkedIn Profile</a>
                  </div>
                )}
              </div>
              
              {/* CV and WeDo CV Generation */}
              <div className="space-y-3">
                {selectedCandidate.cv_url && (
                  <div className="p-3 bg-blue-50 rounded-lg flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <a href={selectedCandidate.cv_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View Original CV / Resume</a>
                  </div>
                )}
                
                {/* WeDo CV Generation */}
                <div className="p-3 bg-gradient-to-r from-[#33AADD]/10 to-[#33AADD]/5 rounded-lg border border-[#33AADD]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-[#33AADD]" />
                    <span className="font-medium text-[#111827]">Generate WeDo CV</span>
                    <Badge variant="outline" className="text-xs">New</Badge>
                  </div>
                  <p className="text-xs text-[#6B7280] mb-3">Create professional, WeDo-branded CVs for client presentation</p>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const url = `${API}/admin/candidates/${selectedCandidate.candidate_id}/generate-wedo-cv?anonymized=false&include_details=true`;
                        window.open(url, '_blank');
                        toast.success('Generating full WeDo CV...');
                      }}
                      data-testid="generate-wedo-cv-full"
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Full Version
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        const url = `${API}/admin/candidates/${selectedCandidate.candidate_id}/generate-wedo-cv?anonymized=true&include_details=true`;
                        window.open(url, '_blank');
                        toast.success('Generating anonymized WeDo CV...');
                      }}
                      data-testid="generate-wedo-cv-anon"
                    >
                      <User className="h-4 w-4 mr-1" />
                      Anonymized Version
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Skills */}
              {selectedCandidate.skills?.length > 0 && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Skills</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.skills.map((s, i) => (
                      <Badge key={s} className="bg-blue-100 text-blue-800">{s}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Languages */}
              {selectedCandidate.languages?.length > 0 && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Languages</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.languages.map((l, i) => (
                      <Badge key={i} variant="outline">{l.language} ({l.level})</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Industry Domains */}
              {selectedCandidate.industry_domains?.length > 0 && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Industry Domains</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.industry_domains.map((d, i) => (
                      <Badge key={d} variant="secondary">{d}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Tags */}
              {selectedCandidate.tags?.length > 0 && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {selectedCandidate.tags.map((t, i) => (
                      <Badge key={i} className="bg-purple-100 text-purple-800">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Applied Jobs */}
              {selectedCandidate.applied_jobs?.length > 0 && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Applied Jobs ({selectedCandidate.application_count || selectedCandidate.applied_jobs.length})</Label>
                  <div className="space-y-2">
                    {selectedCandidate.applied_jobs.map((job, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-[#F9FAFB] rounded">
                        <div>
                          <p className="font-medium text-[#111827]">{job.title}</p>
                          <p className="text-xs text-[#6B7280]">Applied: {job.applied_at ? new Date(job.applied_at).toLocaleDateString() : '-'}</p>
                        </div>
                        <Badge className={
                          job.stage === 'new' ? 'bg-blue-100 text-blue-800' :
                          job.stage === 'screening' ? 'bg-yellow-100 text-yellow-800' :
                          job.stage === 'shortlisted' ? 'bg-purple-100 text-purple-800' :
                          job.stage === 'interview_1' || job.stage === 'interview_2' ? 'bg-orange-100 text-orange-800' :
                          job.stage === 'offer' ? 'bg-cyan-100 text-cyan-800' :
                          job.stage === 'hired' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }>
                          {job.stage?.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Candidate Type Badge */}
              {selectedCandidate.candidate_type && (
                <div className="flex items-center gap-2 pt-2 border-t">
                  <span className="text-sm text-[#6B7280]">Candidate Type:</span>
                  {selectedCandidate.candidate_type === 'job_applicant' && (
                    <Badge className="bg-blue-100 text-blue-800">Job Applicant</Badge>
                  )}
                  {selectedCandidate.candidate_type === 'talent_pool_only' && (
                    <Badge className="bg-gray-100 text-gray-700">Talent Pool Only</Badge>
                  )}
                  {selectedCandidate.candidate_type === 'both' && (
                    <Badge className="bg-purple-100 text-purple-800">Both (Pool + Applicant)</Badge>
                  )}
                </div>
              )}
              
              {/* Notes */}
              {selectedCandidate.recruiter_notes && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Recruiter Notes</Label>
                  <p className="text-[#111827] bg-[#F9FAFB] p-3 rounded-lg whitespace-pre-wrap">{selectedCandidate.recruiter_notes}</p>
                </div>
              )}
              
              {/* Applications */}
              {selectedCandidate.applications?.length > 0 && (
                <div>
                  <Label className="text-[#6B7280] mb-2 block">Job Applications ({selectedCandidate.applications.length})</Label>
                  <div className="space-y-2">
                    {selectedCandidate.applications.map((app) => (
                      <div key={app.application_id} className="p-3 border rounded-lg flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[#111827]">{app.job?.title || 'Unknown Job'}</p>
                          <p className="text-sm text-[#6B7280]">{app.job?.location} • Applied {formatDate(app.applied_at)}</p>
                        </div>
                        <Badge className={`capitalize ${
                          app.pipeline_stage === 'hired' ? 'bg-green-100 text-green-800' :
                          app.pipeline_stage === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {app.pipeline_stage?.replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Meta */}
              <div className="text-xs text-[#9CA3AF] pt-4 border-t">
                Candidate ID: {selectedCandidate.candidate_id} • Created: {formatDate(selectedCandidate.created_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Apply to Job Dialog */}
      <Dialog open={applyToJobDialogOpen} onOpenChange={setApplyToJobDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[#33AADD]" />
              Apply Candidates to Job
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#F0F9FF] rounded-lg">
              <p className="text-sm text-[#111827]">
                <strong>{selectedIds.length}</strong> candidate(s) selected
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Select Job *</Label>
              <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a job..." />
                </SelectTrigger>
                <SelectContent>
                  {jobs.map(job => (
                    <SelectItem key={job.job_id} value={job.job_id}>
                      {job.title} ({job.location})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Recruiter Notes (Optional)</Label>
              <Textarea
                value={bulkNotes}
                onChange={(e) => setBulkNotes(e.target.value)}
                placeholder="Notes for all these applications..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyToJobDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkApplyToJob} disabled={bulkProcessing || !selectedJobId}>
              {bulkProcessing ? 'Processing...' : 'Apply to Job'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Tag Dialog */}
      <Dialog open={bulkTagDialogOpen} onOpenChange={setBulkTagDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tags className="h-5 w-5 text-purple-600" />
              Manage Tags
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-[#F0F9FF] rounded-lg">
              <p className="text-sm text-[#111827]">
                <strong>{selectedIds.length}</strong> candidate(s) selected
              </p>
            </div>
            
            {/* Tags to Add */}
            <div className="space-y-2">
              <Label>Tags to Add</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {bulkTags.add.map((tag, i) => (
                  <Badge key={i} className="bg-green-100 text-green-800 flex items-center gap-1">
                    + {tag}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setBulkTags(prev => ({ ...prev, add: prev.add.filter(t => t !== tag) }))} />
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newBulkTag}
                  onChange={(e) => setNewBulkTag(e.target.value)}
                  placeholder="New tag..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newBulkTag.trim()) {
                      e.preventDefault();
                      if (!bulkTags.add.includes(newBulkTag.trim())) {
                        setBulkTags(prev => ({ ...prev, add: [...prev.add, newBulkTag.trim()] }));
                      }
                      setNewBulkTag('');
                    }
                  }}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    if (newBulkTag.trim() && !bulkTags.add.includes(newBulkTag.trim())) {
                      setBulkTags(prev => ({ ...prev, add: [...prev.add, newBulkTag.trim()] }));
                      setNewBulkTag('');
                    }
                  }}
                >
                  Add
                </Button>
              </div>
            </div>
            
            {/* Tags to Remove */}
            <div className="space-y-2">
              <Label>Tags to Remove</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {bulkTags.remove.map((tag, i) => (
                  <Badge key={i} className="bg-red-100 text-red-800 flex items-center gap-1">
                    - {tag}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setBulkTags(prev => ({ ...prev, remove: prev.remove.filter(t => t !== tag) }))} />
                  </Badge>
                ))}
              </div>
              <Input
                placeholder="Tag to remove..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && e.target.value.trim()) {
                    e.preventDefault();
                    const tag = e.target.value.trim();
                    if (!bulkTags.remove.includes(tag)) {
                      setBulkTags(prev => ({ ...prev, remove: [...prev.remove, tag] }));
                    }
                    e.target.value = '';
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkTagDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkTag} disabled={bulkProcessing}>
              {bulkProcessing ? 'Processing...' : 'Update Tags'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
